// src/pages/AdminDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  Bell,
  BarChart3,
  Building2,
  ChevronRight,
  ClipboardList,
  Clock3,
  CreditCard,
  Download,
  EyeOff,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Menu,
  ReceiptText,
  RefreshCw,
  ScrollText,
  Search,
  ShieldCheck,
  Timer,
  UserPlus,
  UserRound,
  X,
} from "lucide-react";
import { supabase } from "../../supabaseClient";
import AdminPartnerOrganisations from "./PartnerOrganisations";
import AdminSupport from "./AdminSupport";
import { fetchAdminBugReportSummary } from "../../lib/admin/adminSupport";
import AdminNotifications from "./AdminNotifications";
import AdminAudit from "./AdminAudit";

const ADMIN_AREAS = [
  {
    title: "Users",
    description:
      "View learner progress, XP, activity, and current learning position.",
    to: "/admin/users-progress",
    icon: BarChart3,
  },
  {
    title: "Analytics",
    description:
      "Review activation, retention, signups, and recent activity.",
    to: "/admin/analytics",
    icon: Activity,
  },
  {
    title: "Actions & Tasks",
    description:
      "Review practical tasks, learner submissions, and questionnaire answers.",
    to: "/admin/practical-insights",
    icon: ClipboardList,
  },
  {
    title: "Partners",
    description:
      "Manage Partner organisations, access, cohorts, learners, and invitations.",
    to: "/admin/partners",
    icon: Building2,
  },
  {
    title: "Support",
    description:
      "Review technical bugs reported by learners and Partner users.",
    to: "/admin/support",
    icon: LifeBuoy,
    showSupportAlert: true,
  },
  {
    title: "Notifications",
    description:
      "Review notification coverage and send approved push notifications.",
    to: "/admin/notifications",
    icon: Bell,
  },
  {
    title: "Payments",
    description:
      "Review conversion timing and payment lifecycle events.",
    to: "/admin/payments",
    icon: CreditCard,
  },
  {
    title: "Audit",
    description:
      "Review recorded profile, support, notification, follow-up, and payment events.",
    to: "/admin/audit",
    icon: ScrollText,
  },
  {
    title: "Demo Access",
    description:
      "Open real preloaded learner and partner demo accounts for end-to-end testing.",
    to: "/admin/demo-access",
    icon: UserRound,
  },
  {
    title: "Admin Controls",
    description:
      "Manage internal reporting exclusions and operational controls.",
    to: "/admin/admin-controls",
    icon: ShieldCheck,
  },
  
];


const ANALYTICS_CARDS = [
  {
    title: "Activation",
    description:
      "Track new users from signup to their first learning result.",
    to: "/admin/activation",
    icon: Activity,
  },
  {
    title: "Retention",
    description:
      "Track continuation beyond Level 1 and level-to-task timing.",
    to: "/admin/retention",
    icon: Timer,
  },
  {
    title: "Signups",
    description:
      "Review weekly and monthly signup and activation reporting.",
    to: "/admin/signups",
    icon: UserPlus,
  },
  {
    title: "Last Signed In",
    description:
      "Review login gaps and the latest learner activity.",
    to: "/admin/last-signed-in",
    icon: Clock3,
  },
];


const PAYMENT_CARDS = [
  {
    title: "Free to Paid",
    description:
      "Track how long users take to convert after completing free content.",
    to: "/admin/free-to-paid",
    icon: CreditCard,
  },
  {
    title: "Payment Events",
    description:
      "Review PayFast and payment audit events for support and debugging.",
    to: "/admin/payment-events",
    icon: ReceiptText,
  },
];


const ADMIN_CONTROL_CARDS = [
  {
    title: "Reporting Exclusions",
    description:
      "Exclude approved test, office, or internal accounts from dashboard flagging.",
    to: "/admin/reporting-exclusions",
    icon: EyeOff,
  },
];

const ADMIN_NAV_ITEMS = [
  {
    to: "/admin",
    label: "Overview",
    icon: LayoutDashboard,
    exact: true,
    matchPaths: ["/admin"],
  },
  {
    to: "/admin/users-progress",
    label: "Users",
    icon: BarChart3,
    matchPaths: [
      "/admin/users-progress",
    ],
  },
  {
    to: "/admin/analytics",
    label: "Analytics",
    icon: Activity,
    matchPaths: [
      "/admin/analytics",
      "/admin/activation",
      "/admin/retention",
      "/admin/level-timing",
      "/admin/signups",
      "/admin/last-signed-in",
    ],
  },
  {
    to: "/admin/practical-insights",
    label: "Actions & Tasks",
    icon: ClipboardList,
    matchPaths: [
      "/admin/practical-insights",
    ],
  },
  {
    to: "/admin/partners",
    label: "Partners",
    icon: Building2,
    matchPaths: [
      "/admin/partners",
    ],
  },
  {
    to: "/admin/support",
    label: "Support",
    icon: LifeBuoy,
    matchPaths: [
      "/admin/support",
    ],
  },
  {
    to: "/admin/notifications",
    label: "Notifications",
    icon: Bell,
    matchPaths: [
      "/admin/notifications",
    ],
  },
  {
    to: "/admin/payments",
    label: "Payments",
    icon: CreditCard,
    matchPaths: [
      "/admin/payments",
      "/admin/free-to-paid",
      "/admin/payment-events",
    ],
  },
  {
    to: "/admin/audit",
    label: "Audit",
    icon: ScrollText,
    matchPaths: [
      "/admin/audit",
    ],
  },
  {
    to: "/admin/demo-access",
    label: "Demo Access",
    icon: UserRound,
    matchPaths: [
      "/admin/demo-access",
      "/admin/demo-user",
      "/admin/demo-partner",
    ],
  },
  {
    to: "/admin/admin-controls",
    label: "Admin Controls",
    icon: ShieldCheck,
    matchPaths: [
      "/admin/admin-controls",
      "/admin/reporting-exclusions",
    ],
  },
];

const FLAG_STYLES = {
  green: "border-green-200 bg-green-50 text-green-800",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  red: "border-red-200 bg-red-50 text-red-800",
  black: "border-gray-900 bg-gray-900 text-white",
  excluded: "border-slate-200 bg-slate-100 text-slate-600",
  neutral: "border-[#EFEFEF] bg-[#FAFAFA] text-[#757575]",
};

function AdminShell({ user, children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [
    mobileMenuOpen,
    setMobileMenuOpen,
  ] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();

    navigate(
      "/admin/login",
      {
        replace: true,
      }
    );
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [mobileMenuOpen]);

  function isNavItemActive(item) {
    if (item.exact) {
      return location.pathname === item.to;
    }

    return (
      item.matchPaths || [item.to]
    ).some((path) =>
      location.pathname.startsWith(path)
    );
  }

  function AdminNavigation() {
    return (
      <nav
        aria-label="Admin navigation"
        className="flex-1 overflow-y-auto px-3 py-5"
      >
        <div className="space-y-1">
          {ADMIN_NAV_ITEMS.map(
            (item) => {
              const Icon = item.icon;

              const isActive =
                isNavItemActive(item);

              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={[
                    "group flex min-h-[46px] items-center gap-3 rounded-2xl px-4 py-3 text-sm font-black transition",
                    isActive
                      ? "bg-[#FFF0E6] text-[#E9782F]"
                      : "text-[#666666] hover:bg-[#FFF9F5] hover:text-[#333333]",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "flex h-9 w-9 flex-none items-center justify-center rounded-xl transition",
                      isActive
                        ? "bg-[#FF914D] text-white shadow-sm"
                        : "bg-[#FFF7ED] text-[#FF914D] group-hover:bg-[#FFF0E6]",
                    ].join(" ")}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </span>

                  <span className="min-w-0 flex-1 truncate">
                    {item.label}
                  </span>

                  {isActive && (
                    <span className="h-2 w-2 flex-none rounded-full bg-[#FF914D]" />
                  )}
                </Link>
              );
            }
          )}
        </div>
      </nav>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F4] font-sans text-[#333333]">
      <div className="min-h-screen lg:flex">

        {/* ================================================================ */}
        {/* DESKTOP SIDEBAR                                                  */}
        {/* ================================================================ */}

        <aside className="hidden w-[280px] flex-none border-r border-[#FFE1CF] bg-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col">

          {/* Brand */}

          <div className="border-b border-[#FFEDE1] px-5 py-6">
            <Link
              to="/admin"
              className="flex min-w-0 items-center gap-3"
            >
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-[#FF914D] text-white shadow-sm">
                <ShieldCheck className="h-5 w-5" />
              </div>

              <div className="min-w-0">
                <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#FF914D]">
                  Gwin
                </p>

                <p className="mt-0.5 truncate text-base font-black text-[#333333]">
                  Admin
                </p>
              </div>
            </Link>
          </div>


          {/* Navigation */}

          <AdminNavigation />


          {/* Account */}

          <div className="border-t border-[#FFEDE1] p-4">
            <div className="rounded-[22px] bg-[#FFF9F5] p-4">
              <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#FF914D]">
                Admin access
              </p>

              <p className="mt-2 truncate text-sm font-black text-[#333333]">
                {user?.email || "—"}
              </p>

              <div className="mt-2 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-green-500" />

                <p className="text-xs font-semibold text-green-700">
                  Active admin
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleLogout}
              className="mt-3 flex w-full items-center justify-center rounded-2xl border border-[#FFE1CF] bg-white px-4 py-3 text-sm font-black text-[#555555] transition hover:border-[#FF914D] hover:bg-[#FFF4EC] hover:text-[#E9782F]"
            >
              <LogOut className="mr-2 h-4 w-4" />

              Log out
            </button>
          </div>
        </aside>


        {/* ================================================================ */}
        {/* PAGE COLUMN                                                       */}
        {/* ================================================================ */}

        <div className="min-w-0 flex-1">

          {/* ============================================================== */}
          {/* MOBILE HEADER                                                  */}
          {/* ============================================================== */}

          <header className="sticky top-0 z-40 border-b border-[#FFE1CF] bg-white/95 backdrop-blur lg:hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5">

              <Link
                to="/admin"
                className="flex min-w-0 items-center gap-3"
              >
                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[#FF914D] text-white">
                  <ShieldCheck className="h-4 w-4" />
                </div>

                <div className="min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#FF914D]">
                    Gwin
                  </p>

                  <p className="truncate text-sm font-black text-[#333333]">
                    Admin
                  </p>
                </div>
              </Link>

              <button
                type="button"
                onClick={() =>
                  setMobileMenuOpen(true)
                }
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#FFE1CF] bg-[#FFF9F5] text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF4EC]"
                aria-label="Open admin navigation"
                aria-expanded={
                  mobileMenuOpen
                }
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </header>


          {/* ============================================================== */}
          {/* PAGE CONTENT                                                   */}
          {/* ============================================================== */}

          <main className="mx-auto w-full max-w-[1800px] px-4 py-6 sm:px-6 lg:px-8 lg:py-9 xl:px-10">
            {children}
          </main>
        </div>
      </div>


      {/* ================================================================== */}
      {/* MOBILE DRAWER                                                      */}
      {/* ================================================================== */}

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] lg:hidden">

          {/* Backdrop */}

          <button
            type="button"
            aria-label="Close admin navigation"
            onClick={() =>
              setMobileMenuOpen(false)
            }
            className="absolute inset-0 bg-black/35"
          />


          {/* Drawer */}

          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Admin navigation"
            className="absolute inset-y-0 left-0 flex w-[min(320px,88vw)] flex-col bg-white shadow-2xl"
          >

            {/* Drawer header */}

            <div className="flex items-start justify-between gap-3 border-b border-[#FFEDE1] px-5 py-5">
              <Link
                to="/admin"
                className="flex min-w-0 items-center gap-3"
              >
                <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FF914D] text-white">
                  <ShieldCheck className="h-5 w-5" />
                </div>

                <div className="min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#FF914D]">
                    Gwin
                  </p>

                  <p className="truncate text-base font-black text-[#333333]">
                    Admin
                  </p>
                </div>
              </Link>

              <button
                type="button"
                onClick={() =>
                  setMobileMenuOpen(false)
                }
                className="flex h-10 w-10 flex-none items-center justify-center rounded-xl text-[#666666] transition hover:bg-[#FFF4EC] hover:text-[#E9782F]"
                aria-label="Close admin navigation"
              >
                <X className="h-5 w-5" />
              </button>
            </div>


            {/* Drawer navigation */}

            <AdminNavigation />


            {/* Drawer account */}

            <div className="border-t border-[#FFEDE1] p-4">
              <div className="rounded-[20px] bg-[#FFF9F5] p-4">
                <p className="text-[10px] font-black uppercase tracking-[0.15em] text-[#FF914D]">
                  Signed in as
                </p>

                <p className="mt-2 truncate text-sm font-black text-[#333333]">
                  {user?.email || "—"}
                </p>

                <div className="mt-2 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-green-500" />

                  <span className="text-xs font-semibold text-green-700">
                    Active admin
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className="mt-3 flex w-full items-center justify-center rounded-2xl bg-[#FF914D] px-4 py-3 text-sm font-black text-white transition hover:bg-[#E9782F]"
              >
                <LogOut className="mr-2 h-4 w-4" />

                Log out
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function AccessDenied({ user, roles }) {
  return (
    <div className="min-h-screen bg-[#F4F4F4] px-5 py-10 font-sans text-[#333333]">
      <div className="mx-auto max-w-xl rounded-[28px] border border-[#FFE1CF] bg-white p-6 shadow-sm">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
          <ShieldCheck className="h-6 w-6" />
        </div>

        <h1 className="mt-5 text-2xl font-bold">Admin access required</h1>

        <p className="mt-2 text-sm leading-relaxed text-[#757575]">
          This page is only available to approved Gwin administrators.
        </p>

        <div className="mt-5 rounded-2xl border border-[#EFEFEF] bg-[#FAFAFA] p-4 text-sm">
          <p>
            <span className="font-bold text-[#333333]">Signed in as:</span>{" "}
            {user?.email || "Not signed in"}
          </p>

          <p className="mt-1">
            <span className="font-bold text-[#333333]">Admin status:</span>{" "}
            Denied
          </p>

          <p className="mt-1">
            <span className="font-bold text-[#333333]">Roles:</span>{" "}
            {roles.length ? roles.join(", ") : "none"}
          </p>
        </div>

        <p className="mt-5 text-xs leading-relaxed text-[#757575]">
          To grant access, add this user to the{" "}
          <strong>public.user_roles</strong> table with the role{" "}
          <strong>admin</strong>.
        </p>

        <Link
          to="/dashboard"
          className="mt-6 inline-flex rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#E9782F]"
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}

function BackToAdminLink() {
  return (
    <Link
      to="/admin"
      className="text-sm font-bold text-[#FF914D] hover:text-[#E9782F]"
    >
      ← Back to admin
    </Link>
  );
}

function AdminPageHeader({ eyebrow, title, description }) {
  return (
    <header className="mb-7">
      <BackToAdminLink />

      <p className="mt-5 text-xs font-bold uppercase tracking-wider text-[#FF914D]">
        {eyebrow}
      </p>

      <h1 className="mt-2 text-[34px] font-bold leading-tight tracking-[-0.03em] text-[#333333] md:text-[42px]">
        {title}
      </h1>

      <p className="mt-2 max-w-4xl text-sm leading-relaxed text-[#757575] md:text-base">
        {description}
      </p>
    </header>
  );
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-ZA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0";
  return num.toLocaleString("en-ZA");
}

function formatMoney(value, currency = "ZAR") {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";

  return `${currency || "ZAR"} ${num.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDecimal(value, suffix = "") {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";

  return `${num.toLocaleString("en-ZA", {
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}${suffix}`;
}

function formatPercent(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0%";

  return `${num.toLocaleString("en-ZA", {
    minimumFractionDigits: num % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}%`;
}

function formatHours(value) {
  const num = Number(value);

  if (!Number.isFinite(num)) return "—";

  if (num < 1) {
    return `${Math.round(num * 60)} min`;
  }

  if (num < 24) {
    return formatDecimal(num, " hrs");
  }

  const days = Math.floor(num / 24);
  const hours = Math.round(num % 24);

  if (hours <= 0) {
    return days === 1 ? "1 day" : `${days} days`;
  }

  return `${days}d ${hours}h`;
}

function normalizeText(value) {
  return String(value || "").toLowerCase();
}

function safeDateSort(value) {
  const time = new Date(value || "").getTime();
  return Number.isFinite(time) ? time : 0;
}

function getValue(row, column) {
  if (!row || !column) return "";

  if (typeof column.sortAccessor === "function") {
    return column.sortAccessor(row);
  }

  if (typeof column.accessor === "function") {
    return column.accessor(row);
  }

  if (typeof column.accessor === "string") {
    return row[column.accessor];
  }

  return row[column.key];
}

function getAdminSortValue(row, column) {
  const value = getValue(row, column);
  const key = String(column?.key || column?.accessor || "").toLowerCase();

  if (value === null || value === undefined) return "";

  if (value instanceof Date) {
    return value.getTime();
  }

  if (
    key.includes("at") ||
    key.includes("date") ||
    key.includes("time") ||
    key.includes("completed")
  ) {
    const time = safeDateSort(value);
    if (time > 0) return time;
  }

  if (
    key.includes("hours") ||
    key.includes("days") ||
    key.includes("xp") ||
    key.includes("count") ||
    key.includes("rate") ||
    key.includes("percentage") ||
    key.includes("amount") ||
    key.includes("rank") ||
    key.includes("level_number")
  ) {
    return Number(value || 0);
  }

  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? 1 : 0;

  return String(value || "").toLowerCase();
}

function sortRows(rows, columns, sortConfig) {
  const activeColumn =
    columns.find((column) => column.key === sortConfig.key) || columns[0];

  const directionMultiplier = sortConfig.direction === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const aValue = getAdminSortValue(a, activeColumn);
    const bValue = getAdminSortValue(b, activeColumn);

    if (typeof aValue === "number" && typeof bValue === "number") {
      return (aValue - bValue) * directionMultiplier;
    }

    return (
      String(aValue).localeCompare(String(bValue), undefined, {
        numeric: true,
        sensitivity: "base",
      }) * directionMultiplier
    );
  });
}

function formatExportValue(value) {
  if (value === null || value === undefined) return "";

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

function getExportColumns(columns = []) {
  return columns.flatMap((column) => {
    if (!column || column.export === false || column.key === "actions") {
      return [];
    }

    if (column.key === "user") {
      return [
        {
          label: "User name",
          value: (row) => row.full_name || row.name || "Unnamed user",
        },
        {
          label: "Email",
          value: (row) => row.email || "",
        },
        {
          label: "Mobile number",
          value: (row) => row.mobile_number || row.phone || "",
        },
        {
          label: "User ID",
          value: (row) => row.user_id || row.id || "",
        },
      ];
    }

    return [
      {
        label: column.exportLabel || column.label || column.key,
        value: (row) => {
          if (typeof column.exportValue === "function") {
            return column.exportValue(row);
          }

          if (column.exportKey) {
            return row?.[column.exportKey];
          }

          if (typeof column.accessor === "function") {
            return column.accessor(row);
          }

          if (typeof column.accessor === "string") {
            return row?.[column.accessor];
          }

          return row?.[column.key];
        },
      },
    ];
  });
}

function safeWorksheetName(name) {
  const cleaned = String(name || "Export")
    .replace(/[\\/*?:[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return cleaned.slice(0, 31) || "Export";
}

function safeExportFilename(name) {
  const datePart = new Date().toISOString().slice(0, 10);

  const base = String(name || "admin-export")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return `${base || "admin-export"}-${datePart}.xlsx`;
}

function isTextExportColumn(label) {
  return /(email|mobile|phone|user id|id number|registration number|reference|submission)/i.test(
    String(label || "")
  );
}

function isDateExportColumn(label) {
  return /(date|created|updated|completed|signed|occurred|unlocked|answered|last active)/i.test(
    String(label || "")
  );
}

function getExcelCellValue(value, label) {
  if (value === null || value === undefined) {
    return "";
  }

  if (isTextExportColumn(label)) {
    return formatExportValue(value);
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (isDateExportColumn(label) && typeof value === "string") {
    const parsedDate = new Date(value);

    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  return formatExportValue(value);
}

function getCellTextLength(value) {
  if (value === null || value === undefined) {
    return 0;
  }

  if (value instanceof Date) {
    return 20;
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value).length;
    } catch {
      return String(value).length;
    }
  }

  return String(value).length;
}

async function downloadRowsAsExcel({
  title,
  rows = [],
  columns = [],
}) {
  if (!rows.length) {
    alert("There is no data to export yet.");
    return;
  }

  const numberedRows = rows.map((row, index) => ({
    ...row,
    __row_number: index + 1,
  }));

  const exportColumns = [
    {
      label: "No.",
      value: (row) => row.__row_number,
    },
    ...getExportColumns(columns),
  ];

  if (!exportColumns.length) {
    alert("There are no exportable columns for this table.");
    return;
  }

  try {
    const workbook = new ExcelJS.Workbook();

    workbook.creator = "Gwin";
    workbook.company = "Gwin";
    workbook.created = new Date();
    workbook.modified = new Date();

    const worksheet = workbook.addWorksheet(safeWorksheetName(title), {
      views: [
        {
          state: "frozen",
          ySplit: 1,
        },
      ],
    });

    worksheet.addRow(exportColumns.map((column) => column.label));

    numberedRows.forEach((row) => {
      worksheet.addRow(
        exportColumns.map((column) =>
          getExcelCellValue(column.value(row), column.label)
        )
      );
    });

    const headerRow = worksheet.getRow(1);

    headerRow.height = 28;

    headerRow.eachCell((cell) => {
      cell.font = {
        bold: true,
        color: {
          argb: "FFFFFFFF",
        },
      };

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "FFFF914D",
        },
      };

      cell.alignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
      };

      cell.border = {
        bottom: {
          style: "thin",
          color: {
            argb: "FFE9782F",
          },
        },
      };
    });

    worksheet.eachRow(
      {
        includeEmpty: false,
      },
      (row, rowNumber) => {
        if (rowNumber === 1) return;

        row.height = 22;

        row.eachCell(
          {
            includeEmpty: true,
          },
          (cell) => {
            cell.alignment = {
              vertical: "middle",
              horizontal: "left",
              wrapText: true,
            };
          }
        );

        if (rowNumber % 2 === 0) {
          row.eachCell(
            {
              includeEmpty: true,
            },
            (cell) => {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: {
                  argb: "FFFFF7ED",
                },
              };
            }
          );
        }
      }
    );

    exportColumns.forEach((column, index) => {
      const worksheetColumn = worksheet.getColumn(index + 1);

      let longestValue = String(column.label || "").length;

      worksheetColumn.eachCell(
        {
          includeEmpty: true,
        },
        (cell) => {
          longestValue = Math.max(longestValue, getCellTextLength(cell.value));
        }
      );

      worksheetColumn.width = Math.min(Math.max(longestValue + 2, 12), 45);

      if (isTextExportColumn(column.label)) {
        worksheetColumn.numFmt = "@";
      }

      if (isDateExportColumn(column.label)) {
        worksheetColumn.numFmt = "dd mmm yyyy hh:mm";
      }
    });

    worksheet.autoFilter = {
      from: {
        row: 1,
        column: 1,
      },
      to: {
        row: 1,
        column: exportColumns.length,
      },
    };

    worksheet.pageSetup = {
      orientation: exportColumns.length > 8 ? "landscape" : "portrait",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: {
        left: 0.25,
        right: 0.25,
        top: 0.5,
        bottom: 0.5,
        header: 0.2,
        footer: 0.2,
      },
    };

    const workbookBuffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([workbookBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = safeExportFilename(title);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  } catch (error) {
    console.error("Excel export failed:", error);

    alert("The Excel file could not be created. Please try again.");
  }
}

function getDefaultDirection(column) {
  const key = String(column?.key || "").toLowerCase();

  if (
    key.includes("hours") ||
    key.includes("days") ||
    key.includes("xp") ||
    key.includes("count") ||
    key.includes("rank") ||
    key.includes("rate") ||
    key.includes("percentage") ||
    key.includes("amount") ||
    key.includes("completed_at") ||
    key.includes("created_at") ||
    key.includes("signed_up")
  ) {
    return "desc";
  }

  return "asc";
}

function getLocalActivationFlag(hours, excluded = false) {
  const num = Number(hours);

  if (excluded) return "excluded";
  if (!Number.isFinite(num)) return "neutral";
  if (num <= 48) return "green";
  if (num <= 72) return "amber";
  if (num < 240) return "red";

  return "black";
}

function labelForFlag(flag) {
  const value = String(flag || "neutral").toLowerCase();

  if (value === "green") return "Green";
  if (value === "amber") return "Amber";
  if (value === "red") return "Red";
  if (value === "black") return "Black";
  if (value === "excluded") return "Excluded";

  return "Neutral";
}

function FlagBadge({ flag, children }) {
  const value = String(flag || "neutral").toLowerCase();

  return (
    <span
      className={[
        "inline-flex rounded-full border px-3 py-1 text-xs font-black",
        FLAG_STYLES[value] || FLAG_STYLES.neutral,
      ].join(" ")}
    >
      {children || labelForFlag(value)}
    </span>
  );
}

function MetricCell({ value, flag, subtext }) {
  const valueFlag = String(flag || "neutral").toLowerCase();

  return (
    <div
      className={[
        "inline-flex min-w-[120px] flex-col rounded-2xl border px-3 py-2",
        FLAG_STYLES[valueFlag] || FLAG_STYLES.neutral,
      ].join(" ")}
    >
      <span className="text-sm font-black">
        {value}
      </span>

      {subtext && (
        <span className="mt-0.5 text-[10px] font-bold">
          {subtext}
        </span>
      )}
    </div>
  );
}

function followupStatusLabel(status) {
  if (status === "contacted_resolved") {
    return "Contacted - resolved";
  }

  if (status === "contacted_no_response") {
    return "Contacted - no response";
  }

  if (status === "does_not_want_to_pay") {
    return "Does not want to pay/subscribe";
  }

  return "Open";
}

function AdminFollowupControls({
  row,
  followupType,
  snapshotAt,
}) {
  const [status, setStatus] = useState(
    row.followup_status || "open"
  );

  const [note, setNote] = useState(
    row.latest_note || ""
  );

  const [noResponseCount, setNoResponseCount] =
    useState(
      Number(row.no_response_count || 0)
    );

  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] =
    useState("");

  const [error, setError] = useState("");

  useEffect(() => {
    setStatus(row.followup_status || "open");
    setNote(row.latest_note || "");

    setNoResponseCount(
      Number(row.no_response_count || 0)
    );

    setSavedMessage("");
    setError("");
  }, [
    row.user_id,
    row.followup_status,
    row.latest_note,
    row.no_response_count,
  ]);

  const saveFollowup = async () => {
    try {
      setSaving(true);
      setSavedMessage("");
      setError("");

      const { data, error: rpcError } =
        await supabase.rpc(
          "admin_update_dashboard_followup",
          {
            p_user_id: row.user_id,
            p_followup_type: followupType,
            p_activity_snapshot_at:
              snapshotAt || null,
            p_status: status,
            p_note: note || null,
          }
        );

      if (rpcError) throw rpcError;

      const saved = data?.[0];

      if (saved) {
        setStatus(saved.status || status);
        setNote(saved.latest_note || note);

        setNoResponseCount(
          Number(saved.no_response_count || 0)
        );
      }

      setSavedMessage("Saved");
    } catch (err) {
      console.error(
        "Follow-up save error:",
        err
      );

      setError(
        err?.message ||
          "Could not save follow-up."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-w-[320px] rounded-2xl border border-[#EFEFEF] bg-[#FAFAFA] p-3">
      <label className="text-[11px] font-black uppercase tracking-wider text-[#757575]">
        Response
      </label>

      <select
        value={status}
        onChange={(event) =>
          setStatus(event.target.value)
        }
        className="mt-1 w-full rounded-xl border border-[#E5E5E5] bg-white px-3 py-2 text-xs font-bold text-[#333333] outline-none focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
      >
        <option value="open">
          Open
        </option>

        <option value="contacted_resolved">
          Contacted - resolved
        </option>

        <option value="contacted_no_response">
          Contacted - no response
        </option>

        {followupType === "retention" && (
          <option value="does_not_want_to_pay">
            Does not want to pay/subscribe
          </option>
        )}
      </select>

      <label className="mt-3 block text-[11px] font-black uppercase tracking-wider text-[#757575]">
        Admin note
      </label>

      <textarea
        value={note}
        onChange={(event) =>
          setNote(event.target.value)
        }
        placeholder="Add note..."
        rows={3}
        className="mt-1 w-full resize-none rounded-xl border border-[#E5E5E5] bg-white px-3 py-2 text-xs font-semibold text-[#333333] outline-none focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
      />

      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold text-[#757575]">
          Status:{" "}
          <span className="font-black text-[#333333]">
            {followupStatusLabel(status)}
          </span>

          {noResponseCount > 0 && (
            <span className="ml-1 text-red-600">
              · No response: {noResponseCount}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={saveFollowup}
          disabled={saving}
          className="rounded-full bg-[#FF914D] px-4 py-2 text-xs font-black text-white transition hover:bg-[#E9782F] disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      {savedMessage && (
        <p className="mt-2 text-[11px] font-bold text-green-700">
          {savedMessage}
        </p>
      )}

      {error && (
        <p className="mt-2 text-[11px] font-bold text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

function UserCell({ row }) {
  return (
    <div className="min-w-[260px]">
      <p className="font-black text-[#333333]">
        {row.full_name ||
          row.name ||
          "Unnamed user"}
      </p>

      <p className="mt-1 max-w-[260px] truncate text-[11px] font-semibold text-[#757575]">
        {row.user_id || row.id || "—"}
      </p>

      <p className="mt-1 max-w-[260px] truncate text-xs font-semibold text-[#333333]">
        {row.email || "—"}
      </p>

      <p className="mt-1 text-xs text-[#757575]">
        {row.mobile_number ||
          row.phone ||
          "No phone"}
      </p>

      {row.reporting_excluded && (
        <div className="mt-2">
          <FlagBadge flag="excluded">
            Excluded
          </FlagBadge>
        </div>
      )}
    </div>
  );
}

function LevelCell({
  title,
  questId,
  subquestId,
  levelKey,
  completedAt,
  fallback = "No level completed",
}) {
  return (
    <div className="min-w-[260px]">
      <p className="max-w-[320px] font-bold leading-relaxed text-[#333333]">
        {title || fallback}
      </p>

      {(questId ||
        subquestId ||
        levelKey) && (
        <p className="mt-1 text-xs text-[#757575]">
          Quest {questId || "—"} · Subquest{" "}
          {subquestId || "—"} ·{" "}
          {levelKey || "—"}
        </p>
      )}

      {completedAt && (
        <p className="mt-2 text-xs font-semibold text-[#757575]">
          {formatDateTime(completedAt)}
        </p>
      )}
    </div>
  );
}

function SortableTh({
  label,
  columnKey,
  align = "left",
  sortConfig,
  onSort,
}) {
  const active =
    sortConfig.key === columnKey;

  const Icon = !active
    ? ArrowUpDown
    : sortConfig.direction === "asc"
    ? ArrowUp
    : ArrowDown;

  return (
    <th
      className={[
        "px-4 py-4 font-bold",
        align === "right"
          ? "text-right"
          : "text-left",
      ].join(" ")}
    >
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        className={[
          "inline-flex items-center gap-1.5 rounded-full px-2 py-1 transition hover:bg-white hover:text-[#FF914D]",
          align === "right"
            ? "justify-end"
            : "justify-start",
          active ? "text-[#FF914D]" : "",
        ].join(" ")}
      >
        {label}
        <Icon className="h-3.5 w-3.5" />
      </button>
    </th>
  );
}

function AdminTablePage({
  eyebrow,
  title,
  description,
  rpcName,
  rpcParams,
  columns,
  searchPlaceholder,
  searchKeys = [],
  defaultSort,
  minWidth = "1400px",
  emptyMessage = "No rows found.",
  loadingMessage = "Loading rows…",
  mapRow = (row) => row,
  toolbar,
  refreshKey,
}) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  const initialSort = defaultSort || {
    key: columns[0]?.key || "user",
    direction: getDefaultDirection(columns[0]),
  };

  const [sortConfig, setSortConfig] = useState(initialSort);

  const loadRows = async () => {
    try {
      setLoading(true);
      setError("");

      const { data, error: rpcError } = await supabase.rpc(
        rpcName,
        rpcParams || undefined
      );

      if (rpcError) throw rpcError;

      setRows((data || []).map(mapRow));
    } catch (err) {
      console.error(`${rpcName} load error:`, err);
      setError(err?.message || `Could not load ${title}.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpcName, refreshKey]);

  const handleSort = (columnKey) => {
    const column = columns.find((item) => item.key === columnKey);

    setSortConfig((current) => {
      if (current.key === columnKey) {
        return {
          key: columnKey,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }

      return {
        key: columnKey,
        direction: getDefaultDirection(column),
      };
    });
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return rows;

    return rows.filter((row) => {
      const keysToSearch = searchKeys.length
        ? searchKeys
        : columns.map((column) => column.key);

      return keysToSearch.some((key) => normalizeText(row[key]).includes(q));
    });
  }, [rows, search, searchKeys, columns]);

  const sortedRows = useMemo(
    () => sortRows(filteredRows, columns, sortConfig),
    [filteredRows, columns, sortConfig]
  );

  return (
    <>
      <AdminPageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
      />

      <section className="mb-5 rounded-[24px] border border-[#EFEFEF] bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#BCBCBC]" />

            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder || "Search table..."}
              className="w-full rounded-full border border-[#E5E5E5] bg-white py-3 pl-11 pr-4 text-sm font-semibold text-[#333333] outline-none focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {toolbar}

            <button
              type="button"
              onClick={() =>
                downloadRowsAsExcel({
                  title,
                  rows: sortedRows,
                  columns,
                })
              }
              disabled={loading || sortedRows.length === 0}
              className="inline-flex items-center justify-center rounded-full border border-[#FFE1CF] bg-white px-5 py-3 text-sm font-bold text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF4EC] disabled:opacity-60"
            >
              <Download className="mr-2 h-4 w-4" />
              Export Excel
            </button>

            <button
              type="button"
              onClick={loadRows}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#E9782F] disabled:opacity-60"
            >
              <RefreshCw
                className={["mr-2 h-4 w-4", loading ? "animate-spin" : ""].join(
                  " "
                )}
              />
              Refresh
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}

      <section className="overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse text-left text-sm"
            style={{ minWidth }}
          >
            <thead className="bg-[#FFF4EC] text-xs uppercase tracking-wider text-[#757575]">
              <tr>
                <th className="w-[72px] px-4 py-4 text-left font-bold">#</th>

                {columns.map((column) => (
                  <SortableTh
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    align={column.align || "left"}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 py-10 text-center text-sm font-semibold text-[#757575]"
                  >
                    {loadingMessage}
                  </td>
                </tr>
              ) : sortedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="px-4 py-10 text-center text-sm font-semibold text-[#757575]"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                sortedRows.map((row, rowIndex) => (
                  <tr
                    key={row.id || row.user_id || `${title}-${rowIndex}`}
                    className="border-t border-[#EFEFEF] align-top"
                  >
                    <td className="w-[72px] px-4 py-4 font-black text-[#757575]">
                      {rowIndex + 1}
                    </td>

                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={[
                          "px-4 py-4",
                          column.align === "right" ? "text-right" : "",
                        ].join(" ")}
                      >
                        {column.render
                          ? column.render(row)
                          : String(getValue(row, column) || "—")}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function UsersProgressPage() {
  const columns = [
    {
      key: "user",
      label: "User",
      sortAccessor: (row) => row.full_name,
      render: (row) => <UserCell row={row} />,
    },
    {
      key: "progress_rank",
      label: "Progress rank",
      align: "right",
      sortAccessor: (row) =>
        Number(row.progress_rank || row.weekly_rank || 999999),
      exportValue: (row) => row.progress_rank || row.weekly_rank || "",
      render: (row) => {
        const rank = row.progress_rank || row.weekly_rank;

        return rank ? (
          <span className="inline-flex rounded-full bg-[#FFF4EC] px-3 py-1 text-xs font-black text-[#FF914D]">
            #{formatNumber(rank)}
          </span>
        ) : (
          <span className="font-semibold text-[#BCBCBC]">—</span>
        );
      },
    },
    {
      key: "content_position",
      label: "Content position",
      render: (row) => (
        <div className="min-w-[260px]">
          <p className="font-bold text-[#333333]">
            {row.content_position || "Not started"}
          </p>

          <p className="mt-1 text-xs text-[#757575]">
            Quest {row.last_quest_id || "—"} · Subquest{" "}
            {row.last_subquest_id || "—"} · {row.last_level_key || "—"}
          </p>
        </div>
      ),
    },
    {
      key: "last_completed_at",
      label: "Last completed",
      render: (row) => (
        <span className="font-semibold text-[#333333]">
          {formatDateTime(row.last_completed_at)}
        </span>
      ),
    },
    {
      key: "levels_completed_this_week",
      label: "Levels this week",
      align: "right",
      render: (row) => (
        <span className="font-black text-[#333333]">
          {formatNumber(row.levels_completed_this_week)}
        </span>
      ),
    },
    {
      key: "tasks_completed_this_week",
      label: "Tasks this week",
      align: "right",
      render: (row) => (
        <span className="font-black text-[#333333]">
          {formatNumber(row.tasks_completed_this_week)}
        </span>
      ),
    },
    {
      key: "weekly_xp",
      label: "Weekly XP",
      align: "right",
      render: (row) => (
        <span className="font-black text-[#FF914D]">
          {formatNumber(row.weekly_xp)}
        </span>
      ),
    },
    {
      key: "total_xp",
      label: "Total XP",
      align: "right",
      render: (row) => (
        <span className="font-bold text-[#333333]">
          {formatNumber(row.total_xp)}
        </span>
      ),
    },
    {
      key: "task_xp",
      label: "Task XP",
      align: "right",
      render: (row) => (
        <span className="font-bold text-[#333333]">
          {formatNumber(row.task_xp)}
        </span>
      ),
    },
    {
      key: "task_count",
      label: "Tasks done",
      align: "right",
      render: (row) => (
        <span className="font-bold text-[#333333]">
          {formatNumber(row.task_count)}
        </span>
      ),
    },
  ];

  return (
    <AdminTablePage
      eyebrow="Admin · Users Progress"
      title="Users Progress"
      description="Track each learner’s current level, progress rank, weekly activity, total XP, task XP, and latest completed content. Click any column heading to sort."
      rpcName="admin_get_users_progress"
      columns={columns}
      searchPlaceholder="Search by name, email, phone, ID, level, quest, or subquest..."
      searchKeys={[
        "full_name",
        "email",
        "mobile_number",
        "user_id",
        "content_position",
        "last_quest_id",
        "last_subquest_id",
        "last_level_key",
      ]}
      defaultSort={{
        key: "progress_rank",
        direction: "asc",
      }}
      minWidth="1850px"
      loadingMessage="Loading user progress…"
      emptyMessage="No users found."
    />
  );
}

function SignupsPage() {
  const [period, setPeriod] =
    useState("weekly");

  const columns = [
    {
      key: "period_start",
      label: "Period",
      sortAccessor: (row) =>
        row.period_start,
      render: (row) => (
        <div>
          <p className="font-black text-[#333333]">
            {row.period_label ||
              "—"}
          </p>

          <p className="mt-1 text-xs text-[#757575]">
            Starts{" "}
            {formatDate(
              row.period_start
            )}
          </p>
        </div>
      ),
    },
    {
      key: "new_signups",
      label: "New signups",
      align: "right",
      render: (row) => (
        <span className="font-black text-[#333333]">
          {formatNumber(
            row.new_signups
          )}
        </span>
      ),
    },
    {
      key:
        "first_levels_completed",
      label: "First levels",
      align: "right",
      render: (row) => (
        <span className="font-black text-[#333333]">
          {formatNumber(
            row.first_levels_completed
          )}
        </span>
      ),
    },
    {
      key:
        "first_tasks_completed",
      label: "First tasks",
      align: "right",
      render: (row) => (
        <span className="font-black text-[#333333]">
          {formatNumber(
            row.first_tasks_completed
          )}
        </span>
      ),
    },
    {
      key: "activation_rate",
      label: "Activation rate",
      align: "right",
      render: (row) => (
        <span className="rounded-full bg-[#FFF4EC] px-3 py-1 text-xs font-black text-[#FF914D]">
          {formatPercent(
            row.activation_rate
          )}
        </span>
      ),
    },
  ];

  const toolbar = (
    <div className="inline-flex rounded-full border border-[#FFE1CF] bg-[#F4F4F4] p-1">
      {[
        {
          value: "weekly",
          label: "Weekly",
        },
        {
          value: "monthly",
          label: "Monthly",
        },
      ].map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() =>
            setPeriod(option.value)
          }
          className={[
            "rounded-full px-4 py-2 text-sm font-bold transition",
            period === option.value
              ? "bg-[#FF914D] text-white shadow-sm"
              : "text-[#757575] hover:bg-white hover:text-[#333333]",
          ].join(" ")}
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  return (
    <AdminTablePage
      eyebrow="Admin · Signups"
      title="Signups"
      description="View signups by week or month, including how many users completed their first level and first task."
      rpcName="admin_get_signup_report"
      rpcParams={{
        p_period: period,
      }}
      refreshKey={period}
      columns={columns}
      searchPlaceholder="Search by period label..."
      searchKeys={["period_label"]}
      defaultSort={{
        key: "period_start",
        direction: "desc",
      }}
      minWidth="1000px"
      loadingMessage="Loading signup report…"
      emptyMessage="No signup rows found."
      toolbar={toolbar}
    />
  );
}

function ActivationPage() {
  const columns = [
    {
      key: "user",
      label: "User",
      sortAccessor: (row) =>
        row.full_name,
      render: (row) => (
        <UserCell row={row} />
      ),
    },
    {
      key: "signed_up_at",
      label: "Signed up",
      render: (row) =>
        formatDateTime(
          row.signed_up_at
        ),
    },
    {
      key:
        "first_level_completed_at",
      label:
        "First level completed",
      render: (row) =>
        formatDateTime(
          row.first_level_completed_at
        ),
    },
    {
      key:
        "signup_to_first_level_hours",
      label:
        "Signup → first level",
      render: (row) => (
        <MetricCell
          value={formatHours(
            row.signup_to_first_level_hours
          )}
          flag={
            row.signup_to_first_level_flag
          }
          subtext={labelForFlag(
            row.signup_to_first_level_flag
          )}
        />
      ),
    },
    {
      key: "followup_status",
      label: "Admin response",
      sortAccessor: (row) =>
        row.followup_status ||
        "open",
      render: (row) => (
        <AdminFollowupControls
          row={row}
          followupType="activation"
          snapshotAt={
            row.followup_activity_snapshot_at ||
            row.first_level_completed_at ||
            row.signed_up_at
          }
        />
      ),
    },
  ];

  return (
    <AdminTablePage
      eyebrow="Admin · Activation"
      title="Activation"
      description="Track users from signup to first level completion. Flags use 0–48h green, 48–72h amber, 72h–10d red, and 10d+ black."
      rpcName="admin_get_activation_users"
      columns={columns}
      searchPlaceholder="Search by name, email, phone, user ID, response status, or note..."
      searchKeys={[
        "full_name",
        "email",
        "mobile_number",
        "user_id",
        "followup_status",
        "latest_note",
      ]}
      defaultSort={{
        key:
          "signup_to_first_level_hours",
        direction: "desc",
      }}
      minWidth="1500px"
      loadingMessage="Loading activation users…"
      emptyMessage="No activation rows found."
    />
  );
}

function RetentionPage() {
  const columns = [
    {
      key: "user",
      label: "User",
      sortAccessor: (row) =>
        row.full_name,
      render: (row) => (
        <UserCell row={row} />
      ),
    },
    {
      key:
        "completed_levels_count",
      label: "Levels done",
      align: "right",
      render: (row) => (
        <span className="font-black text-[#333333]">
          {formatNumber(
            row.completed_levels_count
          )}
        </span>
      ),
    },
    {
      key:
        "latest_level_completed_at",
      label: "Latest level",
      render: (row) => (
        <LevelCell
          title={
            row.latest_level_title
          }
          questId={
            row.latest_level_quest_id
          }
          subquestId={
            row.latest_level_subquest_id
          }
          levelKey={
            row.latest_level_key
          }
          completedAt={null}
        />
      ),
    },
    {
      key: "last_level_done",
      label: "Last level done",
      sortAccessor: (row) =>
        row.latest_level_completed_at,
      render: (row) => (
        <MetricCell
          value={formatDateTime(
            row.latest_level_completed_at
          )}
          flag={
            row.hours_since_latest_level_flag
          }
          subtext={
            row.hours_since_latest_level
              ? `${formatHours(
                  row.hours_since_latest_level
                )} ago`
              : labelForFlag(
                  row.hours_since_latest_level_flag
                )
          }
        />
      ),
    },
    {
      key:
        "level_plus_task_completed_at",
      label:
        "Level + task completed",
      render: (row) => (
        <MetricCell
          value={
            row.level_plus_task_completed_at
              ? formatDateTime(
                  row.level_plus_task_completed_at
                )
              : "Not completed yet"
          }
          flag={
            row.level_plus_task_flag
          }
          subtext={
            row.level_plus_task_completed_at
              ? labelForFlag(
                  row.level_plus_task_flag
                )
              : "Open"
          }
        />
      ),
    },
    {
      key:
        "hours_since_latest_level",
      label:
        "Last level → Today gap",
      render: (row) => (
        <MetricCell
          value={formatHours(
            row.hours_since_latest_level
          )}
          flag={
            row.hours_since_latest_level_flag
          }
          subtext={labelForFlag(
            row.hours_since_latest_level_flag
          )}
        />
      ),
    },
    {
      key:
        "previous_to_latest_level_hours",
      label: "Level → level gap",
      render: (row) => (
        <MetricCell
          value={formatHours(
            row.previous_to_latest_level_hours
          )}
          flag={
            row.previous_to_latest_level_flag
          }
          subtext={labelForFlag(
            row.previous_to_latest_level_flag
          )}
        />
      ),
    },
    {
      key:
        "latest_level_to_task_hours",
      label: "Level → task gap",
      render: (row) => (
        <MetricCell
          value={formatHours(
            row.latest_level_to_task_hours
          )}
          flag={
            row.latest_level_to_task_flag
          }
          subtext={
            row.latest_level_to_task_is_open
              ? "Open gap"
              : labelForFlag(
                  row.latest_level_to_task_flag
                )
          }
        />
      ),
    },
    {
      key:
        "latest_task_completed_at",
      label: "Matching task",
      render: (row) => (
        <div className="min-w-[260px]">
          <p className="font-bold text-[#333333]">
            {row.latest_task_title ||
              "No matching task completed"}
          </p>

          <p className="mt-1 text-xs text-[#757575]">
            {formatDateTime(
              row.latest_task_completed_at
            )}
          </p>
        </div>
      ),
    },
    {
      key:
        "average_level_to_level_hours",
      label:
        "Avg level → level",
      render: (row) => (
        <span className="font-black text-[#333333]">
          {formatHours(
            row.average_level_to_level_hours
          )}
        </span>
      ),
    },
    {
      key:
        "average_level_to_task_hours",
      label:
        "Avg level → task",
      render: (row) => (
        <span className="font-black text-[#333333]">
          {formatHours(
            row.average_level_to_task_hours
          )}
        </span>
      ),
    },
    {
      key: "followup_status",
      label: "Admin response",
      sortAccessor: (row) =>
        row.followup_status ||
        "open",
      render: (row) => (
        <AdminFollowupControls
          row={row}
          followupType="retention"
          snapshotAt={
            row.followup_activity_snapshot_at ||
            row.latest_level_completed_at ||
            row.updated_at
          }
        />
      ),
    },
  ];

  return (
    <AdminTablePage
      eyebrow="Admin · Retention"
      title="Retention"
      description="Track users beyond Level 1. Flags use 0–24h green, 24–48h amber, 48h–5d red, and 5d+ black. Only the affected metric cell is flagged."
      rpcName="admin_get_retention_users"
      columns={columns}
      searchPlaceholder="Search by name, email, phone, user ID, latest level, task, quest, subquest, status, or note..."
      searchKeys={[
        "full_name",
        "email",
        "mobile_number",
        "user_id",
        "latest_level_title",
        "latest_level_quest_id",
        "latest_level_subquest_id",
        "latest_level_key",
        "latest_task_title",
        "followup_status",
        "latest_note",
      ]}
      defaultSort={{
        key:
          "latest_level_to_task_hours",
        direction: "desc",
      }}
      minWidth="2300px"
      loadingMessage="Loading retention users…"
      emptyMessage="No retention rows found."
    />
  );
}

function LastSignedInPage() {
  const columns = [
    {
      key: "user",
      label: "User",
      sortAccessor: (row) =>
        row.full_name,
      render: (row) => (
        <UserCell row={row} />
      ),
    },
    {
      key: "signed_up_at",
      label: "Signed up",
      render: (row) =>
        formatDateTime(
          row.signed_up_at
        ),
    },
    {
      key: "last_sign_in_at",
      label: "Last signed in",
      render: (row) =>
        formatDateTime(
          row.last_sign_in_at
        ),
    },
    {
      key:
        "hours_since_last_sign_in",
      label: "Login gap",
      render: (row) => {
        const flag =
          getLocalActivationFlag(
            row.hours_since_last_sign_in,
            row.reporting_excluded
          );

        return (
          <MetricCell
            value={formatHours(
              row.hours_since_last_sign_in
            )}
            flag={flag}
            subtext={labelForFlag(
              flag
            )}
          />
        );
      },
    },
    {
      key:
        "latest_level_completed_at",
      label: "Latest level",
      render: (row) => (
        <LevelCell
          title={
            row.latest_level_title
          }
          questId={
            row.latest_level_quest_id
          }
          subquestId={
            row.latest_level_subquest_id
          }
          levelKey={
            row.latest_level_key
          }
          completedAt={
            row.latest_level_completed_at
          }
        />
      ),
    },
    {
      key:
        "latest_task_completed_at",
      label: "Latest task",
      render: (row) => (
        <div className="min-w-[260px]">
          <p className="font-bold text-[#333333]">
            {row.latest_task_title ||
              "No task completed"}
          </p>

          <p className="mt-1 text-xs text-[#757575]">
            {formatDateTime(
              row.latest_task_completed_at
            )}
          </p>
        </div>
      ),
    },
    {
      key:
        "latest_level_to_task_hours",
      label: "Level → task gap",
      render: (row) => (
        <MetricCell
          value={formatHours(
            row.latest_level_to_task_hours
          )}
          flag={
            row.latest_level_to_task_flag
          }
          subtext={labelForFlag(
            row.latest_level_to_task_flag
          )}
        />
      ),
    },
    {
      key:
        "level_plus_task_completed_at",
      label:
        "Level + task completed",
      render: (row) => (
        <MetricCell
          value={formatDateTime(
            row.level_plus_task_completed_at
          )}
          flag={
            row.level_plus_task_flag
          }
          subtext={labelForFlag(
            row.level_plus_task_flag
          )}
        />
      ),
    },
  ];

  return (
    <AdminTablePage
      eyebrow="Admin · Last Signed In"
      title="Last Signed In"
      description="Review when users last signed in and whether their latest level-to-task gap needs attention. Only the affected metric cell is flagged."
      rpcName="admin_get_last_signed_in_users"
      columns={columns}
      searchPlaceholder="Search by name, email, phone, user ID, level, task, quest, or subquest..."
      searchKeys={[
        "full_name",
        "email",
        "mobile_number",
        "user_id",
        "latest_level_title",
        "latest_task_title",
        "latest_level_quest_id",
        "latest_level_subquest_id",
        "latest_level_key",
      ]}
      defaultSort={{
        key:
          "hours_since_last_sign_in",
        direction: "desc",
      }}
      minWidth="1850px"
      loadingMessage="Loading last signed in users…"
      emptyMessage="No last signed in users found."
    />
  );
}

function FreeToPaidPage() {
  const columns = [
    {
      key: "user",
      label: "User",
      sortAccessor: (row) =>
        row.full_name,
      render: (row) => (
        <UserCell row={row} />
      ),
    },
    {
      key:
        "free_level_completed_at",
      label: "Free completed",
      render: (row) =>
        formatDateTime(
          row.free_level_completed_at
        ),
    },
    {
      key: "first_paid_at",
      label: "First paid",
      render: (row) =>
        row.first_paid_at ? (
          <span className="font-semibold text-[#333333]">
            {formatDateTime(
              row.first_paid_at
            )}
          </span>
        ) : (
          <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-black text-amber-700">
            Not paid yet
          </span>
        ),
    },
    {
      key: "hours_to_paid",
      label: "Time to paid",
      render: (row) => (
        <div>
          <p className="font-black text-[#FF914D]">
            {formatHours(
              row.hours_to_paid
            )}
          </p>

          <p className="mt-1 text-xs text-[#757575]">
            {formatDecimal(
              row.days_to_paid,
              " days"
            )}
          </p>
        </div>
      ),
    },
    {
      key: "payment_status",
      label: "Payment status",
      render: (row) =>
        row.payment_status || "—",
    },
    {
      key: "payment_amount",
      label: "Amount",
      align: "right",
      render: (row) => (
        <span className="font-black text-[#333333]">
          {formatMoney(
            row.payment_amount
          )}
        </span>
      ),
    },
    {
      key: "payment_reference",
      label: "Reference",
      render: (row) => (
        <div className="min-w-[320px]">
          <p className="max-w-[320px] truncate font-semibold text-[#333333]">
            {row.payment_reference ||
              "—"}
          </p>

          <p className="mt-1 max-w-[320px] truncate text-xs text-[#757575]">
            {row.product_code || "—"}{" "}
            ·{" "}
            {row.payment_mode || "—"}
          </p>
        </div>
      ),
    },
  ];

  return (
    <AdminTablePage
      eyebrow="Admin · Free to Paid"
      title="Free to Paid"
      description="Track users who completed the free level endpoint and how long they took to become paid users."
      rpcName="admin_get_free_to_paid_timing"
      columns={columns}
      searchPlaceholder="Search by user, email, phone, payment status, product, or reference..."
      searchKeys={[
        "full_name",
        "email",
        "mobile_number",
        "user_id",
        "payment_status",
        "payment_reference",
        "product_code",
      ]}
      defaultSort={{
        key:
          "free_level_completed_at",
        direction: "desc",
      }}
      minWidth="1500px"
      loadingMessage="Loading free-to-paid rows…"
      emptyMessage="No free-to-paid rows found."
    />
  );
}

function PracticalInsightsPage() {
  const [view, setView] =
    useState("users");

  const [users, setUsers] =
    useState([]);

  const [tasks, setTasks] =
    useState([]);

  const [answers, setAnswers] =
    useState([]);

  const [selectedUser, setSelectedUser] =
    useState(null);

  const [selectedTask, setSelectedTask] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [sortConfig, setSortConfig] =
    useState({
      key: "completed_task_count",
      direction: "desc",
    });

  const currentColumns = useMemo(() => {
    if (view === "tasks") {
      return [
        {
          key: "task_title",
          label: "Task",
        },
        {
          key: "level_key",
          label: "Level",
        },
        {
          key: "status",
          label: "Status",
        },
        {
          key: "unlocked_at",
          label: "Unlocked",
        },
        {
          key: "completed_at",
          label: "Completed",
        },
        {
          key: "xp_earned",
          label: "XP",
        },
        {
          key: "submission_id",
          label: "Submission",
        },
        {
          key: "actions",
          label: "Actions",
        },
      ];
    }

    if (view === "answers") {
      return [
        {
          key: "question_text",
          label: "Question",
        },
        {
          key: "question_type",
          label: "Type",
        },
        {
          key:
            "selected_option_text",
          label: "Selected answer",
        },
        {
          key: "text_answer",
          label: "Text answer",
        },
        {
          key: "answer_value",
          label: "Raw value",
        },
        {
          key:
            "answer_created_at",
          label: "Answered at",
        },
      ];
    }

    return [
      {
        key: "full_name",
        label: "User",
      },
      {
        key:
          "completed_task_count",
        label: "Completed tasks",
      },
      {
        key:
          "unlocked_task_count",
        label: "Unlocked tasks",
      },
      {
        key: "task_xp",
        label: "Task XP",
      },
      {
        key: "latest_task_title",
        label: "Latest task",
      },
      {
        key: "latest_level_key",
        label: "Latest level",
      },
      {
        key:
          "latest_task_status",
        label: "Latest status",
      },
      {
        key:
          "latest_task_completed_at",
        label: "Latest completed",
      },
      {
        key: "actions",
        label: "Actions",
      },
    ];
  }, [view]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");

      const { data, error: rpcError } =
        await supabase.rpc(
          "admin_get_practical_user_summary"
        );

      if (rpcError) throw rpcError;

      setUsers(data || []);
      setView("users");
      setSelectedUser(null);
      setSelectedTask(null);
      setTasks([]);
      setAnswers([]);

      setSortConfig({
        key: "completed_task_count",
        direction: "desc",
      });
    } catch (err) {
      console.error(
        "Practical users load error:",
        err
      );

      setError(
        err?.message ||
          "Could not load practical users."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadUserTasks = async (user) => {
    try {
      setLoading(true);
      setError("");
      setSearch("");

      const { data, error: rpcError } =
        await supabase.rpc(
          "admin_get_practical_user_tasks",
          {
            p_user_id: user.user_id,
          }
        );

      if (rpcError) throw rpcError;

      setSelectedUser(user);
      setSelectedTask(null);
      setTasks(data || []);
      setAnswers([]);
      setView("tasks");

      setSortConfig({
        key: "completed_at",
        direction: "desc",
      });
    } catch (err) {
      console.error(
        "Practical tasks load error:",
        err
      );

      setError(
        err?.message ||
          "Could not load user practical tasks."
      );
    } finally {
      setLoading(false);
    }
  };

  const loadTaskAnswers = async (
    task
  ) => {
    try {
      setLoading(true);
      setError("");
      setSearch("");

      const { data, error: rpcError } =
        await supabase.rpc(
          "admin_get_practical_task_answers",
          {
            p_user_id: task.user_id,
            p_practical_challenge_id:
              task.practical_challenge_id,
          }
        );

      if (rpcError) throw rpcError;

      setSelectedTask(task);
      setAnswers(data || []);
      setView("answers");

      setSortConfig({
        key: "question_text",
        direction: "asc",
      });
    } catch (err) {
      console.error(
        "Practical answers load error:",
        err
      );

      setError(
        err?.message ||
          "Could not load practical task answers."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (columnKey) => {
    const column =
      currentColumns.find(
        (item) =>
          item.key === columnKey
      );

    setSortConfig((current) => {
      if (
        current.key === columnKey
      ) {
        return {
          key: columnKey,
          direction:
            current.direction === "asc"
              ? "desc"
              : "asc",
        };
      }

      return {
        key: columnKey,
        direction:
          getDefaultDirection(column),
      };
    });
  };

  const rowsForView = useMemo(() => {
    if (view === "tasks") {
      return tasks;
    }

    if (view === "answers") {
      return answers;
    }

    return users;
  }, [
    view,
    users,
    tasks,
    answers,
  ]);

  const filteredRows = useMemo(() => {
    const q = search
      .trim()
      .toLowerCase();

    if (!q) return rowsForView;

    return rowsForView.filter(
      (row) => {
        if (view === "tasks") {
          return [
            row.task_title,
            row.quest_id,
            row.subquest_id,
            row.level_key,
            row.status,
            row.submission_id,
          ]
            .map(normalizeText)
            .some((value) =>
              value.includes(q)
            );
        }

        if (view === "answers") {
          return [
            row.question_text,
            row.question_type,
            row.question_key,
            row.selected_option_text,
            row.selected_option_value,
            row.text_answer,
            JSON.stringify(
              row.answer_value || {}
            ),
          ]
            .map(normalizeText)
            .some((value) =>
              value.includes(q)
            );
        }

        return [
          row.full_name,
          row.email,
          row.mobile_number,
          row.user_id,
          row.latest_task_title,
          row.latest_quest_id,
          row.latest_subquest_id,
          row.latest_level_key,
          row.latest_task_status,
        ]
          .map(normalizeText)
          .some((value) =>
            value.includes(q)
          );
      }
    );
  }, [
    rowsForView,
    search,
    view,
  ]);

  const sortedRows = useMemo(
    () =>
      sortRows(
        filteredRows,
        currentColumns,
        sortConfig
      ),
    [
      filteredRows,
      currentColumns,
      sortConfig,
    ]
  );

  const goBack = () => {
    setError("");
    setSearch("");

    if (view === "answers") {
      setView("tasks");
      setSelectedTask(null);
      setAnswers([]);

      setSortConfig({
        key: "completed_at",
        direction: "desc",
      });

      return;
    }

    if (view === "tasks") {
      setView("users");
      setSelectedUser(null);
      setSelectedTask(null);
      setTasks([]);
      setAnswers([]);

      setSortConfig({
        key:
          "completed_task_count",
        direction: "desc",
      });
    }
  };

  const pageTitle =
    view === "answers"
      ? "Practical Task Answers"
      : view === "tasks"
      ? "User Practical Tasks"
      : "Practical Insights";

  const pageDescription =
    view === "answers"
      ? "Review the exact questions and answers this user submitted for the selected practical task."
      : view === "tasks"
      ? "Review all practical tasks unlocked or completed by the selected user."
      : "Start with users first, then click into a user to see their practical tasks, and click a task to see the submitted answers.";

  return (
    <>
      <AdminPageHeader
        eyebrow="Admin · Practical Insights"
        title={pageTitle}
        description={pageDescription}
      />

      <section className="mb-5 rounded-[24px] border border-[#EFEFEF] bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div>
            {view !== "users" && (
              <button
                type="button"
                onClick={goBack}
                className="mb-3 inline-flex rounded-full border border-[#FFE1CF] bg-white px-4 py-2 text-sm font-bold text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF4EC]"
              >
                ← Back
              </button>
            )}

            <div className="text-sm text-[#757575]">
              {selectedUser && (
                <p>
                  <span className="font-bold text-[#333333]">
                    User:
                  </span>{" "}
                  {selectedUser.full_name ||
                    "Unnamed user"}{" "}
                  ·{" "}
                  {selectedUser.email ||
                    "No email"}
                </p>
              )}

              {selectedTask && (
                <p className="mt-1">
                  <span className="font-bold text-[#333333]">
                    Task:
                  </span>{" "}
                  {selectedTask.task_title ||
                    "Untitled task"}{" "}
                  · Quest{" "}
                  {selectedTask.quest_id ||
                    "—"}{" "}
                  · Subquest{" "}
                  {selectedTask.subquest_id ||
                    "—"}{" "}
                  ·{" "}
                  {selectedTask.level_key ||
                    "—"}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative min-w-[320px] flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#BCBCBC]" />

              <input
                type="text"
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder={
                  view === "answers"
                    ? "Search questions or answers..."
                    : view ===
                      "tasks"
                    ? "Search tasks, status, level..."
                    : "Search users, email, task, level..."
                }
                className="w-full rounded-full border border-[#E5E5E5] bg-white py-3 pl-11 pr-4 text-sm font-semibold text-[#333333] outline-none focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
              />
            </div>

            <button
              type="button"
              onClick={() =>
                downloadRowsAsExcel({
                  title: pageTitle,
                  rows: sortedRows,
                  columns:
                    currentColumns,
                })
              }
              disabled={
                loading ||
                sortedRows.length === 0
              }
              className="inline-flex items-center justify-center rounded-full border border-[#FFE1CF] bg-white px-5 py-3 text-sm font-bold text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF4EC] disabled:opacity-60"
            >
              <Download className="mr-2 h-4 w-4" />
              Export Excel
            </button>

            <button
              type="button"
              onClick={
                view === "answers" &&
                selectedTask
                  ? () =>
                      loadTaskAnswers(
                        selectedTask
                      )
                  : view ===
                      "tasks" &&
                    selectedUser
                  ? () =>
                      loadUserTasks(
                        selectedUser
                      )
                  : loadUsers
              }
              disabled={loading}
              className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#E9782F] disabled:opacity-60"
            >
              <RefreshCw
                className={[
                  "mr-2 h-4 w-4",
                  loading
                    ? "animate-spin"
                    : "",
                ].join(" ")}
              />
              Refresh
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}

      <section className="overflow-hidden rounded-[24px] border border-[#EFEFEF] bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table
            className="w-full border-collapse text-left text-sm"
            style={{
              minWidth:
                view === "answers"
                  ? "1700px"
                  : view === "tasks"
                  ? "1500px"
                  : "1700px",
            }}
          >
            <thead className="bg-[#FFF4EC] text-xs uppercase tracking-wider text-[#757575]">
              <tr>
                <th className="w-[72px] px-4 py-4 text-left font-bold">#</th>

                {currentColumns.map((column) => (
                  <SortableTh
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    align={column.align || "left"}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={currentColumns.length + 1}
                    className="px-4 py-10 text-center text-sm font-semibold text-[#757575]"
                  >
                    Loading practical
                    insights…
                  </td>
                </tr>
              ) : sortedRows.length ===
                0 ? (
                <tr>
                  <td
                    colSpan={currentColumns.length + 1}
                    className="px-4 py-10 text-center text-sm font-semibold text-[#757575]"
                  >
                    No rows found.
                  </td>
                </tr>
              ) : view === "answers" ? (
                sortedRows.map(
                  (row, index) => (
                    <tr
                      key={
                        row.question_id ||
                        `${row.submission_id}-${index}`
                      }
                      className="border-t border-[#EFEFEF] align-top"
                    >
                      <td className="w-[72px] px-4 py-4 font-black text-[#757575]">
                        {index + 1}
                      </td>

                      <td className="px-4 py-4">
                        <div className="min-w-[420px]">
                          <p className="font-bold leading-relaxed text-[#333333]">
                            {row.question_text ||
                              "—"}
                          </p>

                          <p className="mt-1 text-xs text-[#757575]">
                            {row.question_key ||
                              "—"}
                          </p>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <span className="rounded-full bg-[#FAFAFA] px-3 py-1 text-xs font-black text-[#757575]">
                          {row.question_type ||
                            "—"}
                        </span>
                      </td>

                      <td className="px-4 py-4">
                        <div className="min-w-[300px]">
                          <p className="font-bold text-[#333333]">
                            {row.selected_option_text ||
                              "—"}
                          </p>

                          {row.selected_option_value && (
                            <p className="mt-1 text-xs text-[#757575]">
                              {
                                row.selected_option_value
                              }
                            </p>
                          )}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <p className="max-w-[360px] whitespace-pre-wrap text-sm font-semibold leading-relaxed text-[#333333]">
                          {row.text_answer ||
                            "—"}
                        </p>
                      </td>

                      <td className="px-4 py-4">
                        <pre className="max-h-[180px] min-w-[360px] overflow-auto rounded-2xl bg-[#FAFAFA] p-3 text-xs text-[#333333]">
                          {row.answer_value
                            ? JSON.stringify(
                                row.answer_value,
                                null,
                                2
                              )
                            : "—"}
                        </pre>
                      </td>

                      <td className="px-4 py-4">
                        {formatDateTime(
                          row.answer_created_at
                        )}
                      </td>
                    </tr>
                  )
                )
              ) : view === "tasks" ? (
                sortedRows.map((row, index) => (
                  <tr
                    key={
                      row.practical_challenge_id
                    }
                    className="border-t border-[#EFEFEF] align-top"
                  >
                    <td className="w-[72px] px-4 py-4 font-black text-[#757575]">
                      {index + 1}
                    </td>

                    <td className="px-4 py-4">
                      <div className="min-w-[320px]">
                        <p className="font-black text-[#333333]">
                          {row.task_title ||
                            "Untitled task"}
                        </p>

                        <p className="mt-1 text-xs text-[#757575]">
                          Quest{" "}
                          {row.quest_id ||
                            "—"}{" "}
                          · Subquest{" "}
                          {row.subquest_id ||
                            "—"}{" "}
                          ·{" "}
                          {row.level_key ||
                            "—"}
                        </p>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <span className="font-black text-[#333333]">
                        {row.level_key ||
                          "—"}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={[
                          "rounded-full px-3 py-1 text-xs font-black",
                          row.status ===
                          "completed"
                            ? "bg-green-50 text-green-800"
                            : "bg-amber-50 text-amber-800",
                        ].join(" ")}
                      >
                        {row.status || "—"}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      {formatDateTime(
                        row.unlocked_at
                      )}
                    </td>

                    <td className="px-4 py-4">
                      {formatDateTime(
                        row.completed_at
                      )}
                    </td>

                    <td className="px-4 py-4">
                      <span className="font-black text-[#FF914D]">
                        {formatNumber(
                          row.xp_earned
                        )}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <p className="max-w-[260px] truncate text-xs font-semibold text-[#757575]">
                        {row.submission_id ||
                          "No submission"}
                      </p>
                    </td>

                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={() =>
                          loadTaskAnswers(
                            row
                          )
                        }
                        disabled={
                          !row.submission_id
                        }
                        className="rounded-full bg-[#FF914D] px-4 py-2 text-xs font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:bg-[#E5E5E5] disabled:text-[#999999]"
                      >
                        View answers
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                sortedRows.map((row, index) => (
                  <tr
                    key={row.user_id}
                    className="border-t border-[#EFEFEF] align-top"
                  >
                    <td className="w-[72px] px-4 py-4 font-black text-[#757575]">
                      {index + 1}
                    </td>

                    <td className="px-4 py-4">
                      <UserCell
                        row={row}
                      />
                    </td>

                    <td className="px-4 py-4 text-right">
                      <span className="text-lg font-black text-[#333333]">
                        {formatNumber(
                          row.completed_task_count
                        )}
                      </span>
                    </td>

                    <td className="px-4 py-4 text-right">
                      <span className="text-lg font-black text-[#333333]">
                        {formatNumber(
                          row.unlocked_task_count
                        )}
                      </span>
                    </td>

                    <td className="px-4 py-4 text-right">
                      <span className="text-lg font-black text-[#FF914D]">
                        {formatNumber(
                          row.task_xp
                        )}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="min-w-[320px]">
                        <p className="font-bold text-[#333333]">
                          {row.latest_task_title ||
                            "No practical task yet"}
                        </p>

                        <p className="mt-1 text-xs text-[#757575]">
                          Quest{" "}
                          {row.latest_quest_id ||
                            "—"}{" "}
                          · Subquest{" "}
                          {row.latest_subquest_id ||
                            "—"}{" "}
                          ·{" "}
                          {row.latest_level_key ||
                            "—"}
                        </p>
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <span className="font-black text-[#333333]">
                        {row.latest_level_key ||
                          "—"}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <span
                        className={[
                          "rounded-full px-3 py-1 text-xs font-black",
                          row.latest_task_status ===
                          "completed"
                            ? "bg-green-50 text-green-800"
                            : row.latest_task_status
                            ? "bg-amber-50 text-amber-800"
                            : "bg-[#FAFAFA] text-[#757575]",
                        ].join(" ")}
                      >
                        {row.latest_task_status ||
                          "No task"}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      {formatDateTime(
                        row.latest_task_completed_at
                      )}
                    </td>

                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={() =>
                          loadUserTasks(
                            row
                          )
                        }
                        className="rounded-full bg-[#FF914D] px-4 py-2 text-xs font-black text-white transition hover:bg-[#E9782F]"
                      >
                        View tasks
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function PaymentEventsPage() {
  const columns = [
    {
      key: "created_at",
      label: "Created",
      render: (row) =>
        formatDateTime(
          row.created_at
        ),
    },
    {
      key: "user",
      label: "User",
      sortAccessor: (row) =>
        row.full_name,
      render: (row) => (
        <UserCell row={row} />
      ),
    },
    {
      key: "event_type",
      label: "Event",
      render: (row) => (
        <div className="min-w-[280px]">
          <p className="max-w-[320px] font-bold text-[#333333]">
            {row.event_type || "—"}
          </p>

          <p className="mt-1 text-xs text-[#757575]">
            {row.product_code || "—"}{" "}
            ·{" "}
            {row.payment_mode || "—"}
          </p>
        </div>
      ),
    },
    {
      key: "event_status",
      label: "Status",
      render: (row) => (
        <div>
          <p className="font-bold text-[#333333]">
            {row.event_status || "—"}
          </p>

          <p className="mt-1 text-xs text-[#757575]">
            Purchase:{" "}
            {row.purchase_status ||
              "—"}
          </p>
        </div>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      align: "right",
      render: (row) => (
        <span className="font-black text-[#FF914D]">
          {formatMoney(
            row.amount,
            row.currency
          )}
        </span>
      ),
    },
    {
      key: "m_payment_id",
      label: "Payment IDs",
      render: (row) => (
        <div className="min-w-[300px]">
          <p className="max-w-[300px] truncate text-xs font-semibold text-[#333333]">
            Merchant:{" "}
            {row.m_payment_id ||
              "—"}
          </p>

          <p className="mt-1 max-w-[300px] truncate text-xs text-[#757575]">
            PayFast:{" "}
            {row.pf_payment_id ||
              "—"}
          </p>
        </div>
      ),
    },
    {
      key: "source",
      label: "Source",
      render: (row) => (
        <div>
          <p className="font-bold text-[#333333]">
            {row.source || "—"}
          </p>

          <p className="mt-1 max-w-[220px] truncate text-xs text-[#757575]">
            {row.source_function ||
              "—"}
          </p>
        </div>
      ),
    },
    {
      key: "message",
      label: "Message",
      render: (row) => (
        <p className="max-w-[420px] text-xs font-semibold leading-relaxed text-[#333333]">
          {row.message || "—"}
        </p>
      ),
    },
  ];

  return (
    <AdminTablePage
      eyebrow="Admin · Payment Events"
      title="Payment Events"
      description="Review PayFast and payment audit events for support, debugging, and payment lifecycle checks."
      rpcName="admin_get_payment_events"
      rpcParams={{
        p_limit: 300,
      }}
      columns={columns}
      searchPlaceholder="Search by user, email, event, status, payment ID, product, or message..."
      searchKeys={[
        "full_name",
        "email",
        "event_type",
        "event_status",
        "purchase_status",
        "m_payment_id",
        "pf_payment_id",
        "product_code",
        "message",
      ]}
      defaultSort={{
        key: "created_at",
        direction: "desc",
      }}
      minWidth="1800px"
      loadingMessage="Loading payment events…"
      emptyMessage="No payment events found."
    />
  );
}

function ReportingExclusionsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [reason, setReason] = useState("");
  const [savingUserId, setSavingUserId] = useState("");
  const [formError, setFormError] = useState("");
  const [savedMessage, setSavedMessage] = useState("");

  const saveExclusion = async (row, nextIsActive) => {
    try {
      setSavingUserId(row.user_id);
      setFormError("");
      setSavedMessage("");

      if (!row?.user_id) {
        throw new Error("Could not find this user's ID.");
      }

      const { error } = await supabase.rpc("admin_set_reporting_exclusion", {
        p_user_id: row.user_id,
        p_is_active: nextIsActive,
        p_reason:
          reason?.trim() ||
          row.reason ||
          (nextIsActive ? "Excluded from admin reporting" : "Included again"),
      });

      if (error) throw error;

      setSavedMessage(
        nextIsActive
          ? `${row.full_name || row.email || "User"} has been excluded.`
          : `${row.full_name || row.email || "User"} has been included again.`
      );

      setReason("");
      setRefreshKey((current) => current + 1);
    } catch (err) {
      console.error("Reporting exclusion save error:", err);
      setFormError(err?.message || "Could not update reporting exclusion.");
    } finally {
      setSavingUserId("");
    }
  };

  const columns = [
    {
      key: "user",
      label: "User",
      sortAccessor: (row) => row.full_name,
      render: (row) => <UserCell row={row} />,
    },
    {
      key: "is_active",
      label: "Status",
      sortAccessor: (row) => (row.is_active ? "excluded" : "included"),
      render: (row) => (
        <FlagBadge flag={row.is_active ? "excluded" : "neutral"}>
          {row.is_active ? "Excluded" : "Included"}
        </FlagBadge>
      ),
    },
    {
      key: "reason",
      label: "Reason",
      render: (row) => (
        <p className="max-w-[420px] text-sm font-semibold leading-relaxed text-[#333333]">
          {row.reason || "—"}
        </p>
      ),
    },
    {
      key: "created_at",
      label: "Created",
      render: (row) => formatDateTime(row.created_at),
    },
    {
      key: "updated_at",
      label: "Updated",
      render: (row) => formatDateTime(row.updated_at),
    },
    {
      key: "actions",
      label: "Action",
      export: false,
      render: (row) => {
        const isSaving = savingUserId === row.user_id;

        return (
          <div className="min-w-[190px]">
            {row.is_active ? (
              <button
                type="button"
                onClick={() => saveExclusion(row, false)}
                disabled={isSaving}
                className="rounded-full border border-[#FFE1CF] bg-white px-4 py-2 text-xs font-black text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF4EC] disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Include user"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => saveExclusion(row, true)}
                disabled={isSaving}
                className="rounded-full bg-[#FF914D] px-4 py-2 text-xs font-black text-white transition hover:bg-[#E9782F] disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Exclude user"}
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="text"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Optional reason for selected user"
        className="w-[320px] rounded-full border border-[#E5E5E5] bg-white px-4 py-3 text-sm font-semibold text-[#333333] outline-none focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/15"
      />
    </div>
  );

  return (
    <>
      {formError && (
        <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {formError}
        </div>
      )}

      {savedMessage && (
        <div className="mb-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
          {savedMessage}
        </div>
      )}

      <AdminTablePage
        eyebrow="Admin · Reporting Exclusions"
        title="Reporting Exclusions"
        description="Search for a user, then exclude or include them directly from the table. This does not change app access or make the user an admin."
        rpcName="admin_get_reporting_exclusions"
        columns={columns}
        searchPlaceholder="Search by name, email, phone, user ID, or reason..."
        searchKeys={[
          "full_name",
          "email",
          "mobile_number",
          "user_id",
          "reason",
        ]}
        defaultSort={{
          key: "updated_at",
          direction: "desc",
        }}
        minWidth="1450px"
        loadingMessage="Loading reporting exclusions…"
        emptyMessage="No reporting exclusions found."
        toolbar={toolbar}
        refreshKey={refreshKey}
      />
    </>
  );
}

function AdminSectionLanding({
  eyebrow,
  title,
  description,
  cards,
}) {
  return (
    <>
      <AdminPageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
      />

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map(
          (card) => {
            const Icon =
              card.icon;

            return (
              <Link
                key={
                  card.title
                }
                to={
                  card.to
                }
                className="block"
              >
                <div className="h-full rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm transition hover:border-[#FF914D]/50 hover:shadow-md">
                  <div className="flex items-start justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                      <Icon className="h-5 w-5" />
                    </div>

                    <ChevronRight className="h-5 w-5 text-[#BCBCBC]" />
                  </div>

                  <h2 className="mt-4 text-lg font-bold text-[#333333]">
                    {card.title}
                  </h2>

                  <p className="mt-2 text-sm leading-relaxed text-[#757575]">
                    {card.description}
                  </p>
                </div>
              </Link>
            );
          }
        )}
      </section>
    </>
  );
}


function AnalyticsLandingPage() {
  return (
    <AdminSectionLanding
      eyebrow="Admin · Analytics"
      title="Analytics"
      description="Review how learners enter, activate, continue, and return to Gwin."
      cards={
        ANALYTICS_CARDS
      }
    />
  );
}


function PaymentsLandingPage() {
  return (
    <AdminSectionLanding
      eyebrow="Admin · Payments"
      title="Payments"
      description="Review conversion timing and payment lifecycle information."
      cards={
        PAYMENT_CARDS
      }
    />
  );
}


function AdminControlsLandingPage() {
  return (
    <AdminSectionLanding
      eyebrow="Admin · Controls"
      title="Admin Controls"
      description="Internal operational controls for Gwin administrators."
      cards={
        ADMIN_CONTROL_CARDS
      }
    />
  );
}

function AdminHome({ user, roles }) {
  const adminStatusLabel =
    roles.includes("admin")
      ? "Granted"
      : "Denied";

  const [
    supportSummary,
    setSupportSummary,
  ] =
    useState({
      needs_attention_count: 0,
    });

  useEffect(() => {
    let alive = true;

    async function loadSupportAlert() {
      try {
        const nextSummary =
          await fetchAdminBugReportSummary();

        if (alive) {
          setSupportSummary(
            nextSummary
          );
        }
      } catch (
        error
      ) {
        console.warn(
          "Admin support summary could not be loaded:",
          error
        );
      }
    }

    loadSupportAlert();

    return () => {
      alive = false;
    };
  }, []);

  const supportAlertCount =
    Number(
      supportSummary
        ?.needs_attention_count
    ) || 0;

  return (
    <>
      <header className="mb-7">
        <p className="text-xs font-bold uppercase tracking-wider text-[#FF914D]">
          Admin · Internal use only
        </p>

        <h1 className="mt-2 text-[34px] font-bold leading-tight tracking-[-0.03em] text-[#333333] md:text-[42px]">
          Gwin Admin
        </h1>

        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575] md:text-base">
          Choose a dashboard section
          below. Technical support
          issues requiring attention
          are highlighted on the
          Support card.
        </p>

        <div className="mt-5 rounded-2xl border border-[#FFE1CF] bg-white p-4 text-xs text-[#757575] shadow-sm md:text-sm">
          <div>
            <span className="font-bold text-[#333333]">
              Signed in as:
            </span>{" "}
            {user?.email || "—"}
          </div>

          <div className="mt-1">
            <span className="font-bold text-[#333333]">
              Admin status:
            </span>{" "}
            {adminStatusLabel}
          </div>

          <div className="mt-1">
            <span className="font-bold text-[#333333]">
              Roles:
            </span>{" "}
            {roles.length
              ? roles.join(", ")
              : "none"}
          </div>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
        {ADMIN_AREAS.map((card) => {
          const Icon =
            card.icon;

          const showAlert =
            card.showSupportAlert &&
            supportAlertCount > 0;

          return (
            <Link
              key={card.title}
              to={card.to}
              className="block"
            >
              <div
                className={[
                  "relative h-full rounded-[24px] border bg-white p-5 shadow-sm transition hover:border-[#FF914D]/50 hover:shadow-md",
                  showAlert
                    ? "border-red-200"
                    : "border-[#EFEFEF]",
                ].join(" ")}
              >
                {showAlert && (
                  <div className="absolute right-4 top-4 flex min-w-7 items-center justify-center rounded-full bg-red-600 px-2 py-1 text-xs font-black text-white shadow-sm">
                    {
                      supportAlertCount
                    }
                  </div>
                )}

                <div className="flex items-start justify-between gap-3">
                  <div
                    className={[
                      "flex h-11 w-11 items-center justify-center rounded-2xl",
                      showAlert
                        ? "bg-red-50 text-red-600"
                        : "bg-[#FFF4EC] text-[#FF914D]",
                    ].join(" ")}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  {!showAlert && (
                    <ChevronRight className="h-5 w-5 text-[#BCBCBC]" />
                  )}
                </div>

                <h2 className="mt-4 text-lg font-bold leading-tight text-[#333333]">
                  {card.title}
                </h2>

                <p className="mt-2 text-sm leading-relaxed text-[#757575]">
                  {
                    card.description
                  }
                </p>

                {showAlert && (
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-black text-red-600">
                    <AlertTriangle className="h-3.5 w-3.5" />

                    {supportAlertCount ===
                    1
                      ? "1 issue needs attention"
                      : `${supportAlertCount} issues need attention`}
                  </p>
                )}
              </div>
            </Link>
          );
        })}
      </section>

      <section className="mt-8 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm">
        <h2 className="text-lg font-bold text-[#333333]">
          Quick start
        </h2>

        <p className="mt-1 text-sm leading-relaxed text-[#757575]">
          Start with Activation to see
          whether new users are getting
          started, open Retention to
          check whether users are
          continuing beyond Level 1, or
          open Support when a technical
          issue needs attention.
        </p>

        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to="/admin/activation"
            className="inline-flex items-center gap-2 rounded-full bg-[#FF914D] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#E9782F]"
          >
            Open Activation
            <ArrowRight className="h-4 w-4" />
          </Link>

          <Link
            to="/admin/retention"
            className="inline-flex items-center gap-2 rounded-full border border-[#FFE1CF] bg-white px-5 py-2.5 text-sm font-bold text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF4EC]"
          >
            Open Retention
            <ArrowRight className="h-4 w-4" />
          </Link>

          <Link
            to="/admin/support"
            className="inline-flex items-center gap-2 rounded-full border border-[#FFE1CF] bg-white px-5 py-2.5 text-sm font-bold text-[#333333] transition hover:border-[#FF914D] hover:bg-[#FFF4EC]"
          >
            Open Support

            {supportAlertCount >
              0 && (
              <span className="rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-black text-white">
                {
                  supportAlertCount
                }
              </span>
            )}

            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

function DemoNotice() {
  return (
    <div className="mb-6 rounded-[24px] border border-[#FFE1CF] bg-[#FFF9F5] p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-[#FF914D] text-white">
          <ShieldCheck className="h-5 w-5" />
        </div>

        <div>
          <p className="text-sm font-black text-[#333333]">
            Admin-only real demo account access
          </p>

          <p className="mt-1 text-sm leading-relaxed text-[#757575]">
            This opens a real preloaded test account. It will sign you out of
            the admin session in this tab and log you into the selected demo
            account, so you can test the full learner or partner experience
            exactly as that account sees it.
          </p>

          <p className="mt-2 text-xs font-bold leading-relaxed text-[#9A5A35]">
            Tip: use a private window or separate browser profile if you want
            to keep the admin dashboard open at the same time.
          </p>
        </div>
      </div>
    </div>
  );
}

function RealDemoAccountCard({
  demoType,
  title,
  description,
  icon: Icon,
  buttonLabel,
}) {
  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  async function handleOpenDemoAccount() {
    try {
      setLoading(true);
      setError("");

      const {
        data: {
          session,
        },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        throw sessionError;
      }

      const accessToken =
        session?.access_token;

      if (!accessToken) {
        throw new Error(
          "Your admin session could not be found. Please log in as admin again."
        );
      }

      const response = await fetch(
        "/.netlify/functions/admin-demo-login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            demoType,
          }),
        }
      );

      const result = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result?.error ||
            "Could not start demo account login."
        );
      }

      if (!result?.actionLink) {
        throw new Error(
          "The demo login link was not returned."
        );
      }

      await supabase.auth.signOut();

      window.location.assign(
        result.actionLink
      );
    } catch (openError) {
      console.error(
        "Demo account open failed:",
        openError
      );

      setError(
        openError?.message ||
          "Could not open the demo account."
      );

      setLoading(false);
    }
  }

  return (
    <article className="rounded-[28px] border border-[#FFE1CF] bg-white p-6 shadow-sm">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
        <Icon className="h-6 w-6" />
      </div>

      <h2 className="mt-5 text-xl font-black text-[#333333]">
        {title}
      </h2>

      <p className="mt-2 text-sm leading-relaxed text-[#757575]">
        {description}
      </p>

      {error && (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={handleOpenDemoAccount}
        disabled={loading}
        className="mt-5 inline-flex items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading
          ? "Opening demo account..."
          : buttonLabel}

        {!loading && (
          <ArrowRight className="ml-2 h-4 w-4" />
        )}
      </button>
    </article>
  );
}

function AdminDemoAccessPage() {
  return (
    <>
      <AdminPageHeader
        eyebrow="Admin · Demo Access"
        title="Demo access"
        description="Open real preloaded test accounts for full end-to-end learner and partner testing."
      />

      <DemoNotice />

      <section className="grid gap-5 md:grid-cols-2">
        <RealDemoAccountCard
          demoType="learner"
          title="Open real demo learner"
          description="Log into the preloaded learner test account and test the full learner journey, dashboard, profile gate, lessons, resources, payments, progress, and tasks."
          icon={UserRound}
          buttonLabel="Open learner account"
        />

        <RealDemoAccountCard
          demoType="partner"
          title="Open real demo partner"
          description="Log into the preloaded partner test account and test the full partner portal, cohorts, learners, invitations, reports, support, resources, and settings."
          icon={Building2}
          buttonLabel="Open partner account"
        />
      </section>
    </>
  );
}

export default function AdminDashboard() {
  const location = useLocation();

  const [loading, setLoading] =
    useState(true);

  const [user, setUser] =
    useState(null);

  const [roles, setRoles] =
    useState([]);

  const [roleError, setRoleError] =
    useState("");

  useEffect(() => {
    let alive = true;

    async function loadAdminAccess() {
      try {
        setLoading(true);
        setRoleError("");

        const {
          data: {
            user: authUser,
          },
          error: userError,
        } =
          await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        if (!authUser?.id) {
          if (alive) {
            setUser(null);
            setRoles([]);
          }

          return;
        }

        const {
          data: roleRows,
          error: rolesError,
        } = await supabase
          .from("user_roles")
          .select("role")
          .eq(
            "user_id",
            authUser.id
          );

        if (rolesError) {
          throw rolesError;
        }

        const nextRoles = (
          roleRows || []
        )
          .map((row) =>
            String(row.role || "")
              .trim()
              .toLowerCase()
          )
          .filter(Boolean);

        if (alive) {
          setUser(authUser);

          setRoles(
            Array.from(
              new Set(nextRoles)
            )
          );
        }
      } catch (error) {
        console.error(
          "Admin access load error:",
          error
        );

        if (alive) {
          setRoleError(
            error?.message ||
              "Could not check admin permissions."
          );

          setUser(null);
          setRoles([]);
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    loadAdminAccess();

    return () => {
      alive = false;
    };
  }, []);

  const isAdmin =
    roles.includes("admin");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F4F4] px-5 py-10 font-sans text-center text-sm text-[#757575]">
        Checking admin access…
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <>
        {roleError && (
          <div className="bg-red-50 px-5 py-3 text-center text-sm font-semibold text-red-700">
            {roleError}
          </div>
        )}

        <AccessDenied
          user={user}
          roles={roles}
        />
      </>
    );
  }

      const pathname = location.pathname;

  let page = (
    <AdminHome
      user={user}
      roles={roles}
    />
  );

  /* ------------------------------------------------------------------------ */
  /* Admin demo access                                                        */
  /* ------------------------------------------------------------------------ */

  if (
  pathname.startsWith(
    "/admin/demo-access"
  ) ||
  pathname.startsWith(
    "/admin/demo-user"
  ) ||
  pathname.startsWith(
    "/admin/demo-partner"
  )
) {
  page = (
    <AdminDemoAccessPage />
  );

  /* ------------------------------------------------------------------------ */
  /* Admin section landing pages                                              */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/analytics"
    )
  ) {
    page = (
      <AnalyticsLandingPage />
    );
  } else if (
    pathname.startsWith(
      "/admin/payments"
    )
  ) {
    page = (
      <PaymentsLandingPage />
    );
  } else if (
    pathname.startsWith(
      "/admin/admin-controls"
    )
  ) {
    page = (
      <AdminControlsLandingPage />
    );

  /* ------------------------------------------------------------------------ */
  /* Users                                                                    */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/users-progress"
    )
  ) {
    page = (
      <UsersProgressPage />
    );

  /* ------------------------------------------------------------------------ */
  /* Analytics reports                                                        */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/signups"
    )
  ) {
    page = (
      <SignupsPage />
    );
  } else if (
    pathname.startsWith(
      "/admin/activation"
    )
  ) {
    page = (
      <ActivationPage />
    );
  } else if (
    pathname.startsWith(
      "/admin/retention"
    )
  ) {
    page = (
      <RetentionPage />
    );
  } else if (
    pathname.startsWith(
      "/admin/level-timing"
    )
  ) {
    page = (
      <RetentionPage />
    );
  } else if (
    pathname.startsWith(
      "/admin/last-signed-in"
    )
  ) {
    page = (
      <LastSignedInPage />
    );

  /* ------------------------------------------------------------------------ */
  /* Admin controls                                                           */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/reporting-exclusions"
    )
  ) {
    page = (
      <ReportingExclusionsPage />
    );

  /* ------------------------------------------------------------------------ */
  /* Payments                                                                 */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/free-to-paid"
    )
  ) {
    page = (
      <FreeToPaidPage />
    );

  /* ------------------------------------------------------------------------ */
  /* Actions & Tasks                                                          */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/practical-insights"
    )
  ) {
    page = (
      <PracticalInsightsPage />
    );

  /* ------------------------------------------------------------------------ */
  /* Partners                                                                 */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/partners"
    )
  ) {
    page = (
      <AdminPartnerOrganisations />
    );

  /* ------------------------------------------------------------------------ */
  /* Support                                                                  */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/support"
    )
  ) {
    page = (
      <AdminSupport />
    );

  /* ------------------------------------------------------------------------ */
  /* Notifications                                                            */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/notifications"
    )
  ) {
    page = (
      <AdminNotifications />
    );

  /* ------------------------------------------------------------------------ */
  /* Audit                                                                    */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/audit"
    )
  ) {
    page = (
      <AdminAudit />
    );

  /* ------------------------------------------------------------------------ */
  /* Payment events                                                           */
  /* ------------------------------------------------------------------------ */

  } else if (
    pathname.startsWith(
      "/admin/payment-events"
    )
  ) {
    page = (
      <PaymentEventsPage />
    );
  }

  return (
    <AdminShell user={user}>
      {page}
    </AdminShell>
  );
}